/**
 * The open report's snapshots, and the one it is being compared with.
 *
 * A snapshot is the report's content kept at a moment. Comparing is a mode of
 * the page rather than a screen of its own: one snapshot is chosen as the
 * baseline, and the report is then read against it as it is edited, with
 * what changed marked in the outline and on the page.
 *
 * What changed is recomputed from the report as it is, so it follows each edit.
 * That stays cheap on a long report because a section nobody touched is the same
 * object as before and is remembered (`fingerprint`).
 */

import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import * as reviewsApi from '@/api/reviews'
import * as api from '@/api/snapshots'
import { ApiError, NetworkError } from '@/api/http'
import { readDocument } from '@/domain/report'
import type { Review } from '@/domain/review'
import type { Section } from '@/domain/section'
import type { SnapshotSummary } from '@/domain/snapshot'
import * as tree from '@/domain/tree'
import { diffSections, type ChangeKind } from '@/features/versions/sections'
import { t } from '@/i18n'
import { useDraftsStore } from '@/stores/drafts'
import { useReportStore } from '@/stores/report'

/** The version a report is read against, with the content it kept. */
export interface Baseline {
  readonly summary: SnapshotSummary
  readonly sections: readonly Section[]
}

export const useSnapshotsStore = defineStore('snapshots', () => {
  const drafts = useDraftsStore()
  const report = useReportStore()

  const snapshots = ref<readonly SnapshotSummary[]>([])
  const loading = ref(false)
  const busy = ref(false)
  const problem = ref<string | null>(null)

  const baseline = shallowRef<Baseline | null>(null)
  /** The section whose change is open in the detail view. */
  const inspecting = ref<string | null>(null)

  const draftId = computed(() => drafts.open?.id ?? null)

  /** What differs between the baseline and the report now; null when not comparing. */
  const changes = computed(() =>
    baseline.value ? diffSections(baseline.value.sections, report.sections) : null,
  )

  function changeOf(id: string): ChangeKind | null {
    return changes.value?.changes.get(id) ?? null
  }

  /** What has been decided about the changes, by section: the latest word, stale or not. */
  const reviews = shallowRef<ReadonlyMap<string, Review>>(new Map())

  /** The decision that still stands for this section: none once it was changed again. */
  function reviewOf(id: string): Review | null {
    const found = reviews.value.get(id)
    return found && !found.stale ? found : null
  }

  function accepted(id: string): boolean {
    return reviewOf(id)?.decision === 'accepted'
  }

  /** A change that nobody has accepted yet: what is left to read. */
  function pendingOf(id: string): ChangeKind | null {
    return accepted(id) ? null : changeOf(id)
  }

  /** The sections still to be read, in the order of the document, what is gone after. */
  const pending = computed<string[]>(() => {
    const found = changes.value
    if (!found) return []
    const ids = report.outline.map((row) => row.section.id).filter((id) => pendingOf(id) !== null)
    const gone = found.removed.map((one) => one.section.id).filter((id) => !accepted(id))
    return [...ids, ...gone]
  })

  /** The section as the baseline had it, if it had it. */
  function wasOf(id: string): Section | null {
    const found = (sections: readonly Section[]): Section | null => {
      for (const section of sections) {
        if (section.id === id) return section
        if (section.kind === 'group') {
          const inside = found(section.children)
          if (inside) return inside
        }
      }
      return null
    }
    return baseline.value ? found(baseline.value.sections) : null
  }

  function describe(error: unknown): string {
    if (error instanceof ApiError) return error.message
    if (error instanceof NetworkError) return t('common.unreachable')
    return t('common.problem')
  }

  /** Run one call, saying what went wrong if it did. */
  async function attempt<T>(work: () => Promise<T>): Promise<T | null> {
    busy.value = true
    problem.value = null
    try {
      return await work()
    } catch (error) {
      problem.value = describe(error)
      return null
    } finally {
      busy.value = false
    }
  }

  async function load(): Promise<void> {
    const id = draftId.value
    if (id === null) return
    loading.value = true
    problem.value = null
    try {
      snapshots.value = await api.listSnapshots(id)
    } catch (error) {
      problem.value = describe(error)
    } finally {
      loading.value = false
    }
  }

  /** Keep the report as it is now. Pending edits are saved first, or they would not be in it. */
  async function keep(label: string, comment: string): Promise<SnapshotSummary | null> {
    const id = draftId.value
    if (id === null) return null
    const made = await attempt(async () => {
      await report.flush()
      return api.createSnapshot(id, { label, comment })
    })
    if (made) snapshots.value = [made, ...snapshots.value]
    return made
  }

  async function remove(snapshotId: number): Promise<boolean> {
    const id = draftId.value
    if (id === null) return false
    const done = await attempt(() => api.deleteSnapshot(id, snapshotId))
    if (done) {
      snapshots.value = snapshots.value.filter((one) => one.id !== snapshotId)
      if (baseline.value?.summary.id === snapshotId) baseline.value = null
    }
    return done === true
  }

  /**
   * Put a snapshot's content back as the report's.
   *
   * Pending edits are saved first so that what the server keeps of what is being
   * replaced is complete; the report then adopts what came back, and the history
   * of undo starts afresh since the document it described is gone.
   */
  async function restore(snapshotId: number): Promise<boolean> {
    const id = draftId.value
    if (id === null) return false
    const restored = await attempt(async () => {
      await report.flush()
      return api.restoreSnapshot(id, snapshotId)
    })
    if (!restored) return false

    drafts.open = restored
    report.adopt()
    baseline.value = null
    await load()
    return true
  }

  /** Read the report against a snapshot from now on. */
  async function compareWith(snapshotId: number): Promise<boolean> {
    const id = draftId.value
    if (id === null) return false
    const loaded = await attempt(() => api.loadSnapshot(id, snapshotId))
    if (!loaded) return false

    baseline.value = {
      summary: loaded.summary,
      sections: readDocument(loaded.payload).sections,
    }
    await loadReviews()
    return true
  }

  /** What has been decided so far about the changes read against the baseline. */
  async function loadReviews(): Promise<void> {
    const id = draftId.value
    const base = baseline.value
    if (id === null || !base) {
      reviews.value = new Map()
      return
    }
    try {
      const found = await reviewsApi.listReviews(id, base.summary.id)
      reviews.value = new Map(found.map((one) => [one.sectionId, one]))
    } catch {
      // Reading a trail must not stop the comparison; nothing is marked as read.
      reviews.value = new Map()
    }
  }

  /** The title a section goes by in the trail: what it says, or what it is. */
  function titleOf(id: string): string {
    const section = tree.find(report.sections, id) ?? wasOf(id)
    return section ? section.title.trim() || t(`sections.${section.kind}`) : ''
  }

  /** Send decisions to the server and take back what stands. */
  async function record(
    decisions: readonly { sectionId: string; decision: 'accepted' | 'rejected' }[],
  ): Promise<boolean> {
    const id = draftId.value
    const base = baseline.value
    if (id === null || !base || decisions.length === 0) return false
    const made = await attempt(async () => {
      // What is decided is what is stored: the journal dates the decision against the edits.
      await report.flush()
      return reviewsApi.decide(
        id,
        base.summary.id,
        decisions.map((one) => ({ ...one, title: titleOf(one.sectionId) })),
      )
    })
    if (!made) return false
    reviews.value = new Map(made.map((one) => [one.sectionId, one]))
    return true
  }

  /** Accept changes: they stand, and stop being asked about. */
  function accept(ids: readonly string[]): Promise<boolean> {
    return record(ids.map((sectionId) => ({ sectionId, decision: 'accepted' as const })))
  }

  /**
   * Reject a change: the section goes back to what the baseline had.
   *
   * A section that was changed is put back as it was; one that was added is
   * taken out; one that was removed comes back; one that was moved goes back
   * where it stood. It is one ordinary edit of the report, so it can be undone.
   */
  async function reject(id: string): Promise<boolean> {
    const before = wasOf(id)
    const kind = changeOf(id)
    const gone = kind === null && before !== null && !tree.find(report.sections, id)
    if (!report.editable || (kind === null && !gone)) return false

    if (kind === 'added') {
      report.remove(id)
    } else if (kind === 'modified' || gone) {
      if (!before) return false
      report.restoreSection(before)
    } else if (kind === 'moved') {
      const where = baseline.value ? tree.locate(baseline.value.sections, id) : null
      const parentKept = where?.parentId == null || tree.find(report.sections, where.parentId)
      if (where && parentKept) report.move(id, where)
    }

    return record([{ sectionId: id, decision: 'rejected' }])
  }

  /** The next change still to be read after this one, round to the first, or none. */
  function nextPending(after: string): string | null {
    const ids = pending.value.filter((one) => one !== after)
    if (ids.length === 0) return null
    const order = [
      ...report.outline.map((row) => row.section.id),
      ...(changes.value?.removed ?? []).map((one) => one.section.id),
    ]
    const here = order.indexOf(after)
    return ids.find((one) => order.indexOf(one) > here) ?? ids[0] ?? null
  }

  function stopComparing(): void {
    baseline.value = null
    reviews.value = new Map()
    inspecting.value = null
  }

  function inspect(id: string | null): void {
    inspecting.value = id
  }

  /** Forget everything: another report is opening. */
  function reset(): void {
    snapshots.value = []
    baseline.value = null
    reviews.value = new Map()
    inspecting.value = null
    problem.value = null
  }

  return {
    snapshots,
    loading,
    busy,
    problem,
    baseline,
    changes,
    inspecting,
    changeOf,
    wasOf,
    reviews,
    reviewOf,
    accepted,
    pendingOf,
    pending,
    accept,
    reject,
    nextPending,
    load,
    keep,
    remove,
    restore,
    compareWith,
    stopComparing,
    inspect,
    reset,
  }
})
